import { useCallback, useEffect, useRef, useState } from 'react';

import { config } from '../config';
import { useI18n } from '../i18n/I18nProvider';
import { Icon } from '../ui/Icon';
import { PageFrame } from '../ui/PageFrame';
import { DRAG_SLOP, loadDock, place, saveDock, snap, type Dock } from './dock';
import './game-box.css';

/** 悬浮键边长。≥ 44px：触控目标再小就点不中。和 game-box.css 里的一致。 */
const SIZE = 44;
/** 离层边缘至少留这么多。 */
const MARGIN = 8;

/**
 * 游戏的**外层壳**：游戏铺满整屏，我们自己的 UI 叠在上面。
 *
 * 参考的是 casinoh5 的 `pages/index/gameBox.vue` —— 一个全屏 web-view，
 * 上面 fixed 着返回键（以后还有宝箱、押注返利那些）。这里是同一个结构：
 *
 *     .frame（PageFrame bare）
 *       iframe            游戏本体，铺满，**没有顶栏**
 *       .gamebox          我们的 HUD 层。自己不接事件，只有里面的控件接
 *
 * iframe 还是 PageFrame 开的 —— 限权、卡住兜底、安卓返回键都在那儿，
 * **"整个大厅只有一个开外部页面的口子"这条不变**。这里只管叠在上面的东西。
 *
 * 以后要往游戏里加我们的入口（活动、宝箱、客服），往 `.gamebox` 里放。
 */
export function GameBox({ url, onExit }: { url: string | null; onExit(): void }) {
  const { t } = useI18n();

  // 厂商游戏（PG）里点"退出"会跳到 account 的 /providers/pg/closed，那一页
  // postMessage 通知我们关掉游戏。**只认 account 的源 + 固定的 type** ——
  // iframe 里跑的是别人的页面，别的来源发同样的消息一律不理。
  useEffect(() => {
    if (url === null) return;
    let origin: string;
    try {
      origin = new URL(config.accountUrl).origin;
    } catch {
      return;
    }
    const onMsg = (e: MessageEvent) => {
      const type = (e.data as { type?: unknown } | null)?.type;
      if (e.origin === origin && type === 'slots:exit-game') onExit();
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [url, onExit]);

  return (
    <PageFrame
      open={url !== null}
      url={url ?? ''}
      title={t('frame.game')}
      bare
      onClose={onExit}
      fallback={<p className="small dim">{t('frame.gameBlocked')}</p>}
      overlay={<Hud onExit={onExit} />}
    />
  );
}

function Hud({ onExit }: { onExit(): void }) {
  const { t } = useI18n();
  const layer = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [dock, setDock] = useState<Dock>(
    () => loadDock(typeof localStorage === 'undefined' ? null : localStorage));
  //: 拖动中按钮中心的位置；null = 没在拖，按 dock 摆
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null);
  const start = useRef<{ px: number; py: number; moved: boolean } | null>(null);
  //: 这一下是拖完松手，不是点 —— 挡掉随后那个 click
  const suppressClick = useRef(false);

  // 转屏、收起地址栏都会改层的尺寸。存的是比例，这里按新尺寸重摆
  useEffect(() => {
    const el = layer.current;
    if (!el) return;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const local = useCallback((e: React.PointerEvent) => {
    const r = layer.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    // 捕获住：手指划到 iframe 上方时事件照样归我们，不会被游戏吞掉
    e.currentTarget.setPointerCapture(e.pointerId);
    start.current = { px: e.clientX, py: e.clientY, moved: false };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const s = start.current;
    if (!s) return;
    if (!s.moved && Math.hypot(e.clientX - s.px, e.clientY - s.py) < DRAG_SLOP) return;
    s.moved = true;
    setDrag(local(e));
  };

  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const s = start.current;
    start.current = null;
    if (!s?.moved) return;
    const p = local(e);
    const next = snap(p.x, p.y, box.w, box.h, SIZE, MARGIN);
    setDock(next);
    setDrag(null);
    saveDock(localStorage, next);
    suppressClick.current = true;
  };

  const onPointerCancel = () => { start.current = null; setDrag(null); };

  const onClick = () => {
    if (suppressClick.current) { suppressClick.current = false; return; }
    onExit();
  };

  const pos = drag
    ? { left: drag.x - SIZE / 2, top: drag.y - SIZE / 2 }
    : place(dock, box.w, box.h, SIZE, MARGIN);

  return (
    <div ref={layer} className={`gamebox${drag ? ' gamebox--dragging' : ''}`}>
      {box.h > 0 && (
        <button
          type="button"
          className="gamebox__exit"
          style={{ transform: `translate(${pos.left}px, ${pos.top}px)` }}
          aria-label={t('frame.exitGame')}
          title={t('frame.exitGame')}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onClick={onClick}
        >
          <Icon name="exit" />
        </button>
      )}
    </div>
  );
}
